import { type CSSProperties, useRef, useState } from "react";
import "./VideoReviews.css";

export type VideoReview = {
  author: string;
  videoSource: string;
  profileImageSource?: string;
  thumbnailSource?: string;
  videoReviewText?: string;
};

type VideoReviewsProps = {
  videoReviews: VideoReview[];
};

type IosFullscreenVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};

const getPreviewVideoSource = (videoSource: string) => {
  if (
    videoSource.startsWith("blob:") ||
    videoSource.startsWith("data:") ||
    videoSource.includes("#")
  ) {
    return videoSource;
  }

  return `${videoSource}#t=0.001`;
};

export function VideoReviews({ videoReviews }: VideoReviewsProps) {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [startedReviews, setStartedReviews] = useState<Set<number>>(
    () => new Set(),
  );

  if (!videoReviews.length) return null;

  const reviewListStyle = {
    "--video-review-count": videoReviews.length,
    "--video-review-item-width": `min(411px, calc((100% - ${
      (videoReviews.length - 1) * 8
    }px) / ${videoReviews.length}))`,
  } as CSSProperties;

  const pauseOtherVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, index) => {
      if (index !== activeIndex) video?.pause();
    });
  };

  const markReviewAsStarted = (index: number) => {
    setStartedReviews((currentReviews) => {
      if (currentReviews.has(index)) return currentReviews;

      const nextReviews = new Set(currentReviews);
      nextReviews.add(index);
      return nextReviews;
    });
  };

  const handlePlay = (index: number) => {
    pauseOtherVideos(index);
    markReviewAsStarted(index);
  };

  const requestVideoFullscreen = async (video: HTMLVideoElement) => {
    try {
      if (video.requestFullscreen) {
        await video.requestFullscreen();
        return;
      }
    } catch {
      // Fullscreen can be blocked by browser policy; playback should continue.
    }

    const iosVideo = video as IosFullscreenVideoElement;
    try {
      iosVideo.webkitEnterFullscreen?.();
    } catch {
      // iOS Safari can reject fullscreen if the video is not ready yet.
    }
  };

  const handlePlayRequest = async (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    pauseOtherVideos(index);
    video.controls = true;

    try {
      const playPromise = video.play();
      await requestVideoFullscreen(video);
      await playPromise;
    } catch {
      video.controls = false;
    }
  };

  return (
    <section
      aria-labelledby="react-pdp-video-reviews-heading"
      className="react-pdp__onetime-video-reviews"
    >
      <div className="react-pdp__onetime-video-reviews-heading">
        <span
          aria-hidden="true"
          className="material-symbols-rounded react-pdp__onetime-video-reviews-heading-icon"
        >
          group
        </span>
        <h3 id="react-pdp-video-reviews-heading">Begeisterte Kunden</h3>
      </div>

      <div
        className="react-pdp__onetime-video-review-list"
        style={reviewListStyle}
      >
        {videoReviews.map((videoReview, index) => {
          const hasStarted = startedReviews.has(index);
          const previewVideoSource = videoReview.thumbnailSource
            ? videoReview.videoSource
            : getPreviewVideoSource(videoReview.videoSource);

          return (
            <article
              className={`react-pdp__onetime-video-review${
                hasStarted ? " react-pdp__onetime-video-review--started" : ""
              }`}
              key={`${videoReview.videoSource}-${index}`}
            >
              <video
                aria-label={`Video-Bewertung von ${videoReview.author}`}
                className="react-pdp__onetime-video-review-media"
                controls={hasStarted}
                onPlay={() => handlePlay(index)}
                playsInline
                poster={videoReview.thumbnailSource}
                preload={videoReview.thumbnailSource ? "metadata" : "auto"}
                ref={(video) => {
                  videoRefs.current[index] = video;
                }}
                src={previewVideoSource}
              />

              {!hasStarted ? (
                <>
                  {videoReview.thumbnailSource ? (
                    <img
                      alt=""
                      className="react-pdp__onetime-video-review-thumbnail"
                      src={videoReview.thumbnailSource}
                    />
                  ) : null}

                  <div className="react-pdp__onetime-video-review-author">
                    {videoReview.profileImageSource ? (
                      <img
                        alt=""
                        className="react-pdp__onetime-video-review-profile"
                        src={videoReview.profileImageSource}
                      />
                    ) : null}
                    <span>{videoReview.author}</span>
                  </div>

                  {videoReview.videoReviewText ? (
                    <p className="react-pdp__onetime-video-review-text">
                      {videoReview.videoReviewText}
                    </p>
                  ) : null}

                  <button
                    aria-label={`Video-Bewertung von ${videoReview.author} abspielen`}
                    className="react-pdp__onetime-video-review-play"
                    onClick={() => void handlePlayRequest(index)}
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className="material-symbols-rounded"
                    >
                      play_arrow
                    </span>
                  </button>
                </>
              ) : null}

              <span className="react-pdp__onetime-video-review-handle">
                @halmclub
              </span>
            </article>
          );
        })}
      </div>
    </section>
  );
}
